import icons0 from "./appIcons/icons-0.json";
import icons1 from "./appIcons/icons-1.json";
import icons2 from "./appIcons/icons-2.json";
import icons3 from "./appIcons/icons-3.json";
import icons4 from "./appIcons/icons-4.json";
import icons5 from "./appIcons/icons-5.json";
import icons6 from "./appIcons/icons-6.json";
import icons7 from "./appIcons/icons-7.json";

type IconEntry = { body: string; w: number; h: number; mono?: boolean };

// The app-name → SVG-body map for brand logos: a curated set drawn from the Iconify
// `logos` (full-color) and `simple-icons` (monochrome) sets plus hand-authored glyphs
// for brands in neither pack. This JSON is the source of truth — hand-edit a shard to
// add or change an icon (any shard works; they're all merged below). Split across
// icons-*.json only so no single file trips the repo's 500 KB large-file limit.
// Ghostty embeds its official PNG (ghostty-org/ghostty, images/icons/icon_128.png);
// System Settings, Finder, Activity Monitor, and Docker embed PNGs converted from
// their installed macOS apps' .icns artwork. These assets live in icons-7.json.
const icons = Object.assign(
  {},
  icons0,
  icons1,
  icons2,
  icons3,
  icons4,
  icons5,
  icons6,
  icons7,
) as Record<string, IconEntry>;

// Monochrome (simple-icons) glyphs carry no fill of their own, so they inherit this
// tone; full-color `logos` glyphs set their own fills and ignore it.
const MONO_FILL = "#101012";

// The lookup is case- and whitespace-insensitive: both the icon keys and the incoming
// app name are normalized (lowercased, all whitespace removed) before matching, so
// "Okta"/"okta" and "BambooHR"/"Bamboo HR" resolve to the same logo. Mirrors the
// backend's normalize() in web_app.rs.
const normalize = (s: string): string => s.replace(/\s+/g, "").toLowerCase();

// icons re-keyed by normalized name. The source keys are all distinct after
// normalizing, but should two ever collide the first one inserted wins.
const iconsByNorm = new Map<string, IconEntry>();
for (const [name, entry] of Object.entries(icons)) {
  const key = normalize(name);
  if (!iconsByNorm.has(key)) iconsByNorm.set(key, entry);
}

const uriCache = new Map<string, string | null>();

// An SVG data-URI for an app's brand logo, or null when we have no icon for it (the
// caller then shows the app name in parentheses). Cached per app so a logo repeated
// across many nodes is encoded only once.
export const appIconUri = (app: string | null | undefined): string | null => {
  if (!app) return null;
  const cached = uriCache.get(app);
  if (cached !== undefined) return cached;
  const icon = iconsByNorm.get(normalize(app));
  const uri = icon
    ? `data:image/svg+xml,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${icon.w} ${icon.h}" fill="${MONO_FILL}">${icon.body}</svg>`,
      )}`
    : null;
  uriCache.set(app, uri);
  return uri;
};
